export const KAKAO_JAVASCRIPT_KEY = import.meta.env.VITE_KAKAO_JAVASCRIPT_KEY?.trim() ?? '';

export type KakaoCardShareMode = 'kakao' | 'kakao-ready' | 'unavailable';

export interface KakaoShareOptions {
  readonly objectType: 'feed';
  readonly content: {
    readonly title: string;
    readonly description?: string;
    readonly imageUrl: string;
    readonly link: { readonly mobileWebUrl: string; readonly webUrl: string };
  };
  readonly buttons: readonly {
    readonly title: string;
    readonly link: { readonly mobileWebUrl: string; readonly webUrl: string };
  }[];
}

export interface KakaoSdk {
  init(key: string): void;
  isInitialized(): boolean;
  Share: {
    sendDefault(options: KakaoShareOptions): void;
    uploadImage(options: { file: File[] }): Promise<{ infos: { original: { url: string } } }>;
  };
}

declare global {
  interface Window { Kakao?: KakaoSdk }
}

const KAKAO_SDK_URL = 'https://t1.kakaocdn.net/kakao_js_sdk/2.8.2/kakao.min.js';
const KAKAO_SDK_INTEGRITY = 'sha384-zt/G7/KfaRQ9dT/QIkS0ujMtzouJqzuSJcXVQu50x0rl/+mD1dc70AeOejVbMD9E';
let loadingKakao: Promise<KakaoSdk> | null = null;

function initializedKakao(key: string): KakaoSdk | null {
  const sdk = window.Kakao;
  if (!sdk) return null;
  if (!sdk.isInitialized()) sdk.init(key);
  return sdk;
}

export function loadKakaoSdk(key: string): Promise<KakaoSdk> {
  if (!key) return Promise.reject(new Error('kakao-key-unavailable'));
  try {
    const sdk = initializedKakao(key);
    if (sdk) return Promise.resolve(sdk);
  } catch (error) {
    return Promise.reject(error);
  }
  if (loadingKakao) return loadingKakao;

  const attempt = new Promise<KakaoSdk>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-oneultool-kakao-sdk]');
    const script = existing ?? document.createElement('script');
    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      script.removeEventListener('load', finish);
      script.removeEventListener('error', onError);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      reject(error);
    };
    const finish = () => {
      if (settled) return;
      try {
        const sdk = initializedKakao(key);
        if (!sdk) throw new Error('kakao-sdk-unavailable');
        settled = true;
        cleanup();
        resolve(sdk);
      } catch (error) {
        fail(error);
      }
    };
    const onError = () => fail(new Error('kakao-sdk-load-failed'));
    const timeout = setTimeout(() => fail(new Error('kakao-sdk-load-timeout')), 10_000);
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.src = KAKAO_SDK_URL;
      script.integrity = KAKAO_SDK_INTEGRITY;
      script.crossOrigin = 'anonymous';
      script.dataset.oneultoolKakaoSdk = 'true';
      try { document.head.append(script); } catch (error) { fail(error); }
    }
  });
  loadingKakao = attempt;
  void attempt.catch(() => {
    // A temporary network failure must not poison every later share attempt.
    if (loadingKakao === attempt) loadingKakao = null;
  });
  return attempt;
}

export function preloadKakaoShare(key = KAKAO_JAVASCRIPT_KEY): void {
  if (key) void loadKakaoSdk(key).catch(() => undefined);
}

export async function shareKakaoCard(
  options: KakaoShareOptions,
  key = KAKAO_JAVASCRIPT_KEY,
): Promise<KakaoCardShareMode> {
  if (!key) return 'unavailable';
  try {
    const sdk = initializedKakao(key);
    if (sdk) {
      // Preserve the direct tap. Loading asynchronously requires a new tap.
      sdk.Share.sendDefault(options);
      return 'kakao';
    }
    await loadKakaoSdk(key);
    return 'kakao-ready';
  } catch {
    // Never silently replace a Kakao card with a raw-URL message or clipboard write.
    return 'unavailable';
  }
}

export function shareKakaoInvite(title: string, description: string, url: string): Promise<KakaoCardShareMode> {
  const link = { mobileWebUrl: url, webUrl: url };
  return shareKakaoCard({
    objectType: 'feed',
    content: {
      title,
      description,
      imageUrl: 'https://oneultool.com/brand/oneultool-kakao-share-800x400.png',
      link,
    },
    buttons: [{ title: '함께 게임하기', link }],
  });
}

export function kakaoCardShareMessage(mode: KakaoCardShareMode): string {
  return {
    kakao: '카카오톡 카드 공유 화면을 열었어요.',
    'kakao-ready': '카드 준비가 끝났어요. 공유 버튼을 한 번 더 눌러 주세요.',
    unavailable: '카카오톡 카드 공유를 준비하지 못했어요. 다시 시도하거나 링크 복사를 이용해 주세요.',
  }[mode];
}
